import type {
  CommandContribution,
  IconSpec,
  ResolvedCommand,
  WhenContext,
} from '@hibro/shared';
import { evaluateWhen } from '@hibro/shared';

/**
 * Resolves a contributed `%key%` placeholder against its module's catalog —
 * structurally the renderer `Translator.title`, taken as a parameter so this
 * module stays free of React context.
 */
export type TitleResolver = (moduleId: string, raw: string) => string;

/**
 * Whether a command is opted into user-facing lists (架构方案 §5.9).
 *
 * Fail-closed: omitted or `true` means machinery. Only an explicit `false`
 * lists the command. `executeCommand` never consults this.
 */
export function isListedCommand(
  command: Pick<CommandContribution, 'internal'>,
): boolean {
  return command.internal === false;
}

/**
 * Whether a command's own availability condition holds (架构方案 §5.9).
 *
 * Command-level, so every face has to agree on it — the palette, a keybinding
 * and a menu all ask this one question, then each applies whatever `when` is
 * attached to its own entry on top. Purely presentational: a programmatic
 * `executeCommand` never consults it.
 */
export function isCommandEnabled(
  command: Pick<CommandContribution, 'enablement'>,
  context: WhenContext,
): boolean {
  return evaluateWhen(command.enablement, context);
}

/**
 * What to call a command that some other declaration points at — a keybinding,
 * a menu entry — given the full command table.
 *
 * **The title has to be read off the declaring command, never rebuilt from its
 * id.** A `%key%` placeholder is scoped to the module that declared it
 * (`<moduleId>:<key>`), so a key assembled out of an id addresses a catalog
 * entry nobody wrote and degrades to printing that key at the user
 * (`command.reloadWindowHard`, 实测报过). This is also why the platform's own
 * commands must be in the table handed here: they are declared exactly like a
 * module's, and leaving them out is what makes them the ones that break.
 *
 * An id nobody declared returns as-is: that is genuinely all that is known
 * about it, and inventing a prettier label would hide a dangling reference.
 */
export function resolveCommandLabel(
  commandId: string,
  commands: readonly ResolvedCommand[],
  resolveTitle: TitleResolver,
): string {
  const command = commands.find((candidate) => candidate.id === commandId);
  return command ? resolveTitle(command.moduleId, command.title) : commandId;
}

/**
 * The one-line explanation of what a command does, or `undefined` when its
 * author never wrote one.
 *
 * `undefined` rather than a stand-in: `description` is optional and most
 * commands still omit it, so a face that needs a second line has to decide for
 * itself what to say instead — and inventing prose here would put words in the
 * declaring module's mouth.
 *
 * There is deliberately no per-keybinding description to fall back to. A chord
 * carries no meaning of its own; it means whatever its command means, so a
 * second sentence attached to the binding could only repeat this one or
 * contradict it (见 03「快捷键子系统」).
 */
export function resolveCommandDescription(
  commandId: string,
  commands: readonly ResolvedCommand[],
  resolveTitle: TitleResolver,
): string | undefined {
  const command = commands.find((candidate) => candidate.id === commandId);
  if (!command?.description) {
    return undefined;
  }
  return resolveTitle(command.moduleId, command.description);
}

/**
 * One command prepared for display: every `%key%` already resolved, and the
 * two hints a presentation face cannot work out on its own — whether the entry
 * came from recent history, and whether its label collides with another's.
 *
 * Deliberately carries no layout decision (no grouping, no truncation, no
 * ordering weight beyond the array order): which face shows what is the host's
 * call, not the command's (架构方案 §5.9).
 */
export interface CommandCandidate {
  readonly id: string;
  readonly moduleId: string;
  /** Translated title, without the category prefix. */
  readonly title: string;
  /** Translated category, absent when the command declared none. */
  readonly category?: string;
  /** `Category: Title`, or just the title — what a face shows as the entry. */
  readonly label: string;
  readonly description?: string;
  readonly icon?: IconSpec;
  /**
   * True when another visible command renders the same {@link label}. Faces show
   * the raw id only for these, so the common case stays free of id noise while
   * genuine collisions remain distinguishable.
   */
  readonly ambiguous: boolean;
  /** True when the command is in the recent-use history. */
  readonly recent: boolean;
}

export interface CommandQueryOptions {
  /** All known commands, built-ins included. */
  readonly commands: readonly ResolvedCommand[];
  /** Raw user input; blank lists everything. */
  readonly query: string;
  /** Context the `enablement` clauses are evaluated against. */
  readonly context: WhenContext;
  readonly resolveTitle: TitleResolver;
  /** Recently used ids, most recent first (see {@link createCommandMru}). */
  readonly mru?: readonly string[];
}

/** Split a `%key%`-or-literal keyword declaration into individual terms. */
function resolveKeywords(
  command: ResolvedCommand,
  resolveTitle: TitleResolver,
): string[] {
  const raw = command.keywords;
  if (raw === undefined) {
    return [];
  }
  // Both separators are in play: a translated catalog entry is one comma-joined
  // string (one key per command reads better for translators than one per
  // synonym), while a literal declaration is already an array.
  const parts = Array.isArray(raw) ? raw : [raw];
  return parts
    .flatMap((part) => resolveTitle(command.moduleId, part).split(/[,，]/))
    .map((keyword) => keyword.trim())
    .filter((keyword) => keyword.length > 0);
}

/**
 * All terms must appear somewhere in the haystack, so typing `theme dark`
 * finds `Appearance: Use dark theme` regardless of word order — the behaviour
 * users expect from a search box, and the reason matching cannot be left to
 * cmdk's single-string filter.
 */
export function matchesAllTerms(
  terms: readonly string[],
  haystack: string,
): boolean {
  return terms.every((term) => haystack.includes(term));
}

/** Split raw user input into the terms {@link matchesAllTerms} requires. */
export function queryTerms(query: string): string[] {
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter((term) => term.length > 0);
}

/**
 * The text a command is findable by: its category, title, synonyms and id.
 *
 * Exported so a second face can search commands the *same* way the command list
 * does. `keywords` is the part that makes this worth sharing — it exists purely
 * so a command answers to words that appear nowhere in its title, and a face
 * that builds its own haystack silently loses them (the shortcuts page found
 * nothing for `palette` while the search box found the command, 实测报过).
 *
 * `description` is deliberately absent, matching the command list: it is prose
 * written to be read once a row is on screen, and folding whole sentences in
 * here would let unrelated commands match a common word.
 */
export function commandSearchText(
  command: ResolvedCommand,
  resolveTitle: TitleResolver,
): string {
  const category =
    command.category === undefined
      ? undefined
      : resolveTitle(command.moduleId, command.category);
  return [
    category,
    resolveTitle(command.moduleId, command.title),
    ...resolveKeywords(command, resolveTitle),
    command.id,
  ]
    .filter((part): part is string => part !== undefined)
    .join(' ')
    .toLowerCase();
}

/**
 * Filter + rank commands for any user-facing face (架构方案 §5.9). Pure: the
 * caller supplies the context, the translator and the history, so the same
 * function serves the palette today and the global search box later.
 *
 * Excluded here rather than at `listContributions`, which stays the full
 * contribution aggregate that menus and failure diagnostics read:
 * - commands that did not opt in with `internal: false` (default-hidden);
 * - commands whose `enablement` does not hold.
 *
 * Ranking is recency then alphabetical. No relevance scoring: VS Code's TF-IDF
 * pass exists to rescue recall across a thousand commands, which is not the
 * problem at this scale.
 */
export function queryCommands({
  commands,
  query,
  context,
  resolveTitle,
  mru = [],
}: CommandQueryOptions): CommandCandidate[] {
  const terms = queryTerms(query);
  const mruRank = new Map(mru.map((id, index) => [id, index]));

  const matched: Omit<CommandCandidate, 'ambiguous'>[] = [];
  for (const command of commands) {
    if (!isListedCommand(command)) {
      continue;
    }
    if (!isCommandEnabled(command, context)) {
      continue;
    }
    const title = resolveTitle(command.moduleId, command.title);
    const category =
      command.category === undefined
        ? undefined
        : resolveTitle(command.moduleId, command.category);
    if (!matchesAllTerms(terms, commandSearchText(command, resolveTitle))) {
      continue;
    }
    matched.push({
      id: command.id,
      moduleId: command.moduleId,
      title,
      ...(category !== undefined ? { category } : {}),
      label: category === undefined ? title : `${category}: ${title}`,
      ...(command.description !== undefined
        ? { description: resolveTitle(command.moduleId, command.description) }
        : {}),
      ...(command.icon !== undefined ? { icon: command.icon } : {}),
      recent: mruRank.has(command.id),
    });
  }

  const labelCounts = new Map<string, number>();
  for (const entry of matched) {
    labelCounts.set(entry.label, (labelCounts.get(entry.label) ?? 0) + 1);
  }

  return matched
    .map((entry) => ({
      ...entry,
      ambiguous: (labelCounts.get(entry.label) ?? 0) > 1,
    }))
    .sort((a, b) => {
      const rankA = mruRank.get(a.id) ?? Number.MAX_SAFE_INTEGER;
      const rankB = mruRank.get(b.id) ?? Number.MAX_SAFE_INTEGER;
      return rankA === rankB ? a.label.localeCompare(b.label) : rankA - rankB;
    });
}

/** Where the recent-command history is persisted. */
export const COMMAND_MRU_STORAGE_KEY = 'workbench.commandPalette.mru';

/** How many ids the history keeps; older entries fall off the end. */
export const COMMAND_MRU_LIMIT = 50;

/** Minimal slice of the Web Storage API the history needs. */
export type CommandMruStorage = Pick<Storage, 'getItem' | 'setItem'>;

export interface CommandMru {
  /** Recently used ids, most recent first. */
  list(): readonly string[];
  /** Move `id` to the front of the history and persist. */
  record(id: string): void;
}

/**
 * Recent-command history (架构方案 §5.9).
 *
 * Persisted in Web Storage rather than through the extension host, which is a
 * deliberate read of VS Code's experience (microsoft/vscode#194491): they first
 * synced the MRU live across windows, found that jumping between windows made
 * the recent list shift underfoot, and settled on writing it back at shutdown.
 * Web Storage is shared by every window of the session yet notifies none of
 * them, which is exactly that semantic — and it keeps a purely presentational
 * ordering from needing a cross-process channel.
 *
 * `storage` is injected so tests and storage-less hosts (a sandboxed frame, a
 * headless render) get a working no-op instead of a throw.
 */
export function createCommandMru(
  storage: CommandMruStorage | undefined = globalThis.localStorage,
): CommandMru {
  const read = (): string[] => {
    try {
      const raw = storage?.getItem(COMMAND_MRU_STORAGE_KEY);
      if (raw === null || raw === undefined) {
        return [];
      }
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((id): id is string => typeof id === 'string')
        : [];
    } catch {
      // A corrupt or unavailable store must not take the palette down with it;
      // losing the ordering is the whole cost.
      return [];
    }
  };

  return {
    list: read,
    record: (id) => {
      const next = [id, ...read().filter((entry) => entry !== id)].slice(
        0,
        COMMAND_MRU_LIMIT,
      );
      try {
        storage?.setItem(COMMAND_MRU_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Quota or a locked-down store: the history simply does not persist.
      }
    },
  };
}
